import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { StyledBookingFlowButton } from "../../containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { Loading, WellnessSuccessAvatar } from "../../../components";
import { useModal } from "../../../context/ModalContext";
import { PAGES_PATH } from "../../../navigation/params";
import { userActivityLogger } from "../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { CloseIcon } from "../../../components/Icons";
import { useGetAutoRenewalPreference } from "../../../services/renewal-preference/renewal-preference.service";
import { fromISOToFormatDate } from "../../../utils";

interface IScratchPayRenewalPendingModalProps {
  petId: string;
  petName: string;
  petImage?: string;
  currentSubscriptionId: string;
}

const ScratchPayRenewalPendingModal = () => {
  const navigate = useNavigate();
  const { closeModal, params } = useModal();

  const {
    petId,
    petName,
    petImage,
    currentSubscriptionId,
  } = params as unknown as IScratchPayRenewalPendingModalProps;

  const { preference, loading } = useGetAutoRenewalPreference(currentSubscriptionId);

  const handleGotIt = () => {
    userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_RENEWAL_PENDING_GOT_IT_CLICKED, {
      pet_id: petId,
      pet_name: petName,
      plan_name: preference?.newPlanName,
      payment_method: "ScratchPay",
    });

    handleClose();
  };

  const handleClose = () => {
    closeModal();
    navigate(PAGES_PATH.USER_PROFILE + "/" + PAGES_PATH.USER_BILLING);
  };

  useEffect(() => {
    userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_RENEWAL_PENDING_MODAL_VIEWED, {
      pet_id: petId,
      pet_name: petName,
      plan_name: preference?.newPlanName,
      payment_method: "ScratchPay",
    });
  }, []);

  if (loading || !preference) {
    return (
      <Box className="bg-white flex flex-col justify-center items-center gap-5 w-full rounded-3xl">
        <Loading />
      </Box>
    );
  }

  return (
    <Box className="bg-white flex flex-col justify-center items-center gap-5 w-full rounded-3xl  xl:w-[33rem]">
      <Box className="flex justify-end w-full relative -top-3 -right-2">
        <Box
          className="bg-gray-6 rounded-full p-1 cursor-pointer"
          onClick={handleClose}
        >
          <CloseIcon className="text-light-black" />
        </Box>
      </Box>

      <WellnessSuccessAvatar image={petImage || ""} name={petName} />

      <Typography
        textAlign="center"
        variant="h2"
        className="!font-semibold !text-[30px] !leading-[32px] grenette-pro"
      >
        Your Preference Has Been Saved!
      </Typography>

      <Box className="flex flex-col gap-4 w-full">
        <Typography
          textAlign="center"
          className="!text-base !leading-6 !w-full text-light-black px-4"
        >
          Monthly plans are processed through our partner Scratch. Renewal
          requires a new plan purchase at the end of your current term. We will
          remind you when it&apos;s time.
        </Typography>

        {preference?.newPlanStartDate && (
          <Typography
            textAlign="center"
            className="!text-sm !leading-5 text-gray-1 px-4"
          >
            Your current plan ends on {fromISOToFormatDate(preference?.newPlanStartDate)}
          </Typography>
        )}
      </Box>

      <StyledBookingFlowButton
        fullWidth
        color="primary"
        size="small"
        className="!mt-2"
        onClick={handleGotIt}
      >
        Got it
      </StyledBookingFlowButton>
    </Box>
  );
};

export default ScratchPayRenewalPendingModal;

